import type { ApolloClient, ErrorLike, ObservableQuery, OperationVariables } from '@apollo/client';
import type { useQuery } from '@apollo/client/react';

import type { PageInfo } from '@zen/types';
import type { Optional, Undefinable } from '@zen/utils/typescript';

export interface BasicQueryResult<NodeType> {
  nodes: NodeType[];
  pageInfo: PagePosition;
  totalCount: number;
}

export enum PaginationDirection {
  AFTER = 'after',
  BEFORE = 'before'
}

export type UseQueryHookType<Result, Variables extends OperationVariables> = (
  args: useQuery.Options<Result, Variables>
) => useQuery.Result<Result, Variables>;

export type PaginatedVariables = {
  after?: Optional<string>;
  before?: Optional<string>;
  first?: Optional<number>;
  last?: Optional<number>;
};

export interface PaginatedQueryResult<Result, Variables extends OperationVariables, NodeType = unknown> {
  error: Undefinable<ErrorLike>;
  loading: boolean;
  nodes: NodeType[];
  pageRefresh?: () => void;
  paginationInfo: PageInfo;
  refetch: (variables?: Partial<Variables>) => Promise<ApolloClient.QueryResult<Result>>;
  totalCount: number;
}

export type PaginatedQueryHookResult<Result, Variables extends OperationVariables, NodeType> = Omit<
  PaginatedQueryResult<Result, Variables, NodeType>,
  'paginationInfo'
> & { fetchMore: useQuery.Result<Result, Variables>['fetchMore']; pageInfo: PagePosition };

export type PagePosition = { endCursor?: string; hasNextPage: boolean; hasPreviousPage: boolean; startCursor?: string };

export type PaginationQueryHookVariables<Variables> = Omit<Variables, 'first' | 'last' | 'after' | 'before'>;

export type UpdateQueryType<
  TData = unknown,
  TFetchData = unknown,
  TVariables extends OperationVariables = OperationVariables
> = NonNullable<ObservableQuery.FetchMoreOptions<TData, TVariables, TFetchData>['updateQuery']>;
